/**
 * The browser's realtime conversation client.
 *
 * ── WHY THIS IS NOT A REACT HOOK ───────────────────────────────────────────────
 *
 * Two of its responsibilities cannot tolerate the React render cycle. Barge-in
 * has to stop audio the instant speech is detected, and microphone frames have
 * to leave on the audio clock's schedule. Routing either through setState adds a
 * render's worth of latency to something measured in tens of milliseconds — so
 * the protocol lives here and React merely observes it.
 *
 * ── IT CONNECTS ONLY TO BEHEARD ────────────────────────────────────────────────
 *
 * Under Architecture B the server holds the provider socket. This client has no
 * OpenAI credential, ephemeral or otherwise, and nothing here reads or stores
 * one. The browser is a microphone, a speaker and a screen.
 */

import { SpeechResampler, encodePcm16, decodePcm16, PROVIDER_RATE } from '@beheard/shared/realtime';

export type RealtimeUiState =
  | 'idle' | 'connecting' | 'ready' | 'listening' | 'thinking'
  | 'speaking' | 'reconnecting' | 'ending' | 'completed' | 'error';

export interface RealtimeClientEvents {
  state: (s: RealtimeUiState) => void;
  partialTranscript: (text: string) => void;
  finalTranscript: (text: string) => void;
  assistantText: (text: string) => void;
  error: (code: string) => void;
  micPermission: (granted: boolean) => void;
}

/**
 * Microphone backpressure, in seconds of provider audio.
 *
 * At 24 kHz PCM16 mono one second is 48,000 bytes, so these are small numbers:
 * 250 ms of backlog is about 12 kB. Expressing them as seconds keeps the
 * meaning legible — how far behind real time the socket may fall.
 */
const MIC_LOW_WATER_BYTES = 0.25 * PROVIDER_RATE * 2;   // ~12 kB
const MIC_HIGH_WATER_BYTES = 1.0 * PROVIDER_RATE * 2;   // ~48 kB
const MIC_HARD_LIMIT_BYTES = 3.0 * PROVIDER_RATE * 2;   // ~144 kB

/** ~40 ms per packet: small enough for conversation, large enough to amortise framing. */
const CAPTURE_PACKET_SAMPLES = 960;
/** The realtime handshake (backend realtimeCredential.ts): protocol name + credential, never a URL token. */
const REALTIME_SUBPROTOCOL = 'beheard.realtime.v1';
const AUTH_SUBPROTOCOL_PREFIX = 'beheard.auth.';
const JWT_SHAPE = /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/;

/** Local speech detection — for STOPPING AUDIO ONLY, never for transcripts. */
const SPEECH_RMS_THRESHOLD = 0.035;
const SPEECH_MIN_FRAMES = 3;
/** Ignore the first moments of assistant speech, when echo is most likely. */
const ECHO_COOLDOWN_MS = 250;

export class RealtimeClient {
  private ws: WebSocket | null = null;
  private ctx: AudioContext | null = null;
  private captureNode: AudioWorkletNode | null = null;
  private playbackNode: AudioWorkletNode | null = null;
  private stream: MediaStream | null = null;
  private resampler: SpeechResampler | null = null;

  private pendingPcm: number[] = [];
  private micSequence = 0;
  private micPaused = false;
  private degradedFrom: number | null = null;

  private activeResponse: string | null = null;
  private assistantStartedAt = 0;
  private speechFrames = 0;
  private interruptSent = false;
  /**
   * A previous response's audio is still draining.
   *
   * Local speech detection is gated on an ACTIVE response, so once a response
   * settled the user could not stop its still-audible tail — they had to repeat
   * themselves until the provider's own VAD reacted. Audible audio must always
   * be interruptible, whatever the response lifecycle says.
   */
  private tailDraining = false;

  private state: RealtimeUiState = 'idle';
  private reconnectAttempts = 0;
  /** Diagnostics only: the retry number and previous close code, reported on reconnect. */
  private reconnectAttemptsMade = 0;
  private lastCloseCode: number | null = null;
  /** A terminal failure has released the resources; never reconnect after it. */
  private fatal = false;
  private closedByUser = false;

  constructor(
    private readonly opts: {
      url: string;
      token: string;
      sessionId: string;
      handlers: Partial<RealtimeClientEvents>;
      maxReconnects?: number;
    }
  ) {}

  get uiState(): RealtimeUiState {
    return this.state;
  }

  private setState(s: RealtimeUiState): void {
    this.state = s;
    this.opts.handlers.state?.(s);
  }

  // ── lifecycle ────────────────────────────────────────────────────────────

  async start(): Promise<void> {
    this.closedByUser = false;
    this.setState('connecting');
    try {
      await this.openAudio();
    } catch (err) {
      // A refused microphone is an ordinary outcome, not a crash. The user is
      // told plainly and Record & Review remains available.
      //
      // Release whatever was acquired before the failure. openAudio() opens the
      // microphone FIRST and loads the worklet modules after, so a module that
      // will not load throws with the microphone already live — and nothing on
      // this path would ever have stopped it. Teardown is safe when nothing was
      // acquired: every field it touches is null.
      await this.teardown();
      this.opts.handlers.micPermission?.(false);
      this.opts.handlers.error?.(
        (err as Error)?.name === 'NotAllowedError' ? 'MIC_DENIED' : 'MIC_UNAVAILABLE'
      );
      this.setState('error');
      return;
    }
    this.opts.handlers.micPermission?.(true);
    this.connect();
  }

  async stop(): Promise<void> {
    this.closedByUser = true;
    this.setState('ending');
    // An End pressed while the socket is still connecting used to be dropped
    // here (sendControl sends only on an OPEN socket) and the attempt recorded
    // as a disconnect instead of the End the user chose. Wait, briefly, for the
    // socket to open so the End is actually said; the microphone is released
    // below either way.
    if (this.ws?.readyState === WebSocket.CONNECTING) await this.untilOpen(this.ws, RealtimeClient.END_OPEN_WAIT_MS);
    this.sendControl({ type: 'realtime.end' });
    await this.teardown();
    this.setState('completed');
  }

  private async teardown(): Promise<void> {
    this.disarmLiveness();
    this.captureNode?.port.postMessage({ type: 'stop' });
    this.captureNode?.disconnect();
    this.playbackNode?.disconnect();
    this.stream?.getTracks().forEach((t) => t.stop());
    if (this.ctx && this.ctx.state !== 'closed') await this.ctx.close();
    this.ws?.close();
    this.captureNode = null;
    this.playbackNode = null;
    this.stream = null;
    this.ctx = null;
    this.ws = null;
  }

  // ── audio ────────────────────────────────────────────────────────────────

  private async openAudio(): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        // Echo cancellation is what stops the assistant's own voice from
        // repeatedly triggering the local barge-in detector.
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        channelCount: 1,
      },
    });

    // A device can vanish mid-conversation; say so rather than going quiet.
    this.stream.getAudioTracks()[0]?.addEventListener('ended', () => {
      this.opts.handlers.error?.('MIC_LOST');
      this.setState('error');
    });

    this.ctx = new AudioContext();
    // Read the REAL rate. Requesting one does not oblige the browser to give it.
    const inputRate = this.ctx.sampleRate;
    this.resampler = new SpeechResampler(inputRate, PROVIDER_RATE);

    await this.ctx.audioWorklet.addModule('/worklets/capture-processor.js');
    await this.ctx.audioWorklet.addModule('/worklets/playback-processor.js');

    this.captureNode = new AudioWorkletNode(this.ctx, 'capture-processor');
    this.captureNode.port.onmessage = (e) => this.onCapture(e.data);
    this.ctx.createMediaStreamSource(this.stream).connect(this.captureNode);

    this.playbackNode = new AudioWorkletNode(this.ctx, 'playback-processor', {
      processorOptions: { outputRate: this.ctx.sampleRate },
      outputChannelCount: [1],
    });
    this.playbackNode.port.onmessage = (e) => this.onPlayback(e.data);
    this.playbackNode.connect(this.ctx.destination);
  }

  private onCapture(msg: { type: string; samples: Float32Array }): void {
    if (msg.type !== 'capture' || !this.resampler) return;

    this.detectLocalSpeech(msg.samples);

    const resampled = this.resampler.process(msg.samples);
    for (let i = 0; i < resampled.length; i++) this.pendingPcm.push(resampled[i] as number);

    while (this.pendingPcm.length >= CAPTURE_PACKET_SAMPLES) {
      const frame = Float32Array.from(this.pendingPcm.splice(0, CAPTURE_PACKET_SAMPLES));
      this.sendMicPacket(encodePcm16(frame));
    }
  }

  /**
   * Local speech detection, used ONLY to stop playback quickly.
   *
   * It is not canonical VAD and never produces a transcript. The provider's own
   * VAD remains the speech boundary; this exists because waiting for a server
   * round trip to stop audio is perceptible.
   */
  private detectLocalSpeech(samples: Float32Array): void {
    // Audible audio is interruptible, whether or not a response is active: a
    // settled response's tail is still the assistant talking.
    if ((this.activeResponse === null && !this.tailDraining) || this.interruptSent) return;
    if (Date.now() - this.assistantStartedAt < ECHO_COOLDOWN_MS) return;

    let sum = 0;
    for (let i = 0; i < samples.length; i++) sum += (samples[i] as number) ** 2;
    const rms = Math.sqrt(sum / Math.max(1, samples.length));

    if (rms < SPEECH_RMS_THRESHOLD) {
      this.speechFrames = 0;
      return;
    }
    // Hysteresis: a single loud frame is a door closing, not speech.
    if (++this.speechFrames < SPEECH_MIN_FRAMES) return;

    this.bargeIn();
  }

  /** Stop audio first, tell the server second, update the UI last. */
  private bargeIn(): void {
    this.interruptSent = true;
    // Clears the current queue AND the carried tail: an intentional
    // interruption is about the conversation, not just the newest sentence.
    this.playbackNode?.port.postMessage({ type: 'flush' });
    this.tailDraining = false;
    this.setState('listening');
  }

  private onPlayback(msg: Record<string, unknown>): void {
    if (msg.type === 'signal' && msg.signal === 'tail_drained') {
      this.tailDraining = false;
      return;
    }
    if (msg.type === 'progress') {
      // Only a FLUSH is an interruption. A hand-off is the last progress report
      // for a response that ended on its own, and sending it as an interrupt
      // would make the server cancel and truncate a reply nobody interrupted.
      if (msg.reason === 'handoff') this.tailDraining = true;
      // Only a FLUSH — of the queue or of the carried tail — is the user
      // cutting audio off. A tail that played to its end, or was cut by the
      // tail cap, is a reply's FINAL figure but nobody interrupted it: it goes
      // as progress, marked final, so the server can settle that reply's
      // delivery without treating it as a barge-in.
      const interrupted = msg.reason === 'flush' || msg.reason === 'tail_flush';
      const final = msg.final === true;
      this.sendControl({
        type: interrupted ? 'playback.interrupt' : 'playback.progress',
        ...(final && !interrupted ? { final: true } : {}),
        responseRef: msg.responseRef as string,
        // No itemRef. The browser is never told the provider's item id, so the
        // old `?? ''` put an EMPTY STRING in a field the server validated as a
        // non-empty string — every progress frame was rejected as
        // bad_field_type the moment the assistant first spoke, and the session
        // died. Item identity is the server's, resolved from the provider.
        renderedSamples: msg.renderedProviderSamples as number,
        sequence: msg.sequence as number,
      });
    }
  }

  // ── socket ───────────────────────────────────────────────────────────────

  private connect(): void {
    // ── THE CREDENTIAL IS NEVER IN THE URL ─────────────────────────────────────
    //
    // It rides in Sec-WebSocket-Protocol as `beheard.auth.<token>` alongside the
    // protocol name; the server selects only the name. A token that is not
    // JWT-shaped is refused here rather than handed to the WebSocket
    // constructor (which throws on characters a subprotocol cannot hold).
    if (!JWT_SHAPE.test(this.opts.token)) {
      this.fail('AUTH_FAILED');
      return;
    }
    const url = `${this.opts.url}?sessionId=${encodeURIComponent(this.opts.sessionId)}`;
    const ws = new WebSocket(url, [REALTIME_SUBPROTOCOL, `${AUTH_SUBPROTOCOL_PREFIX}${this.opts.token}`]);
    ws.binaryType = 'arraybuffer';
    this.ws = ws;

    ws.onopen = () => {
      this.reconnectAttempts = 0;
      // The playback worklet outlives a connection — openAudio() runs once per
      // start(), so a reconnect reuses it. Stale state made the browser report
      // progress for a response the NEW server session had never heard of: 27
      // refusals in one physical session. Correctly refused, never should have
      // been sent.
      this.playbackNode?.port.postMessage({ type: 'reset' });
      this.activeResponse = null;
      // Say how this connection came about, so a drop can be read from both
      // ends: which retry this is and how the last socket closed.
      this.sendControl({
        type: 'realtime.start',
        ...(this.lastCloseCode !== null
          ? { reconnectAttempt: this.reconnectAttemptsMade, lastCloseCode: this.lastCloseCode }
          : {}),
      });
    };
    this.armLiveness(ws);
    ws.onmessage = (e) => {
      this.lastInboundAt = Date.now();
      // Direction disambiguates the binary channel: anything binary arriving
      // here is assistant audio, because microphone frames only ever go out.
      if (e.data instanceof ArrayBuffer) this.onAssistantAudio(e.data);
      else this.onServerControl(String(e.data));
    };
    ws.onerror = () => this.opts.handlers.error?.('CONNECTION_ERROR');
    ws.onclose = (e) => {
      this.disarmLiveness();
      this.lastCloseCode = e.code;
      // teardown() closes this socket, so without the fatal guard a terminal
      // failure would schedule a reconnect from inside its own cleanup.
      if (this.closedByUser || this.fatal) return;
      this.scheduleReconnect();
    };
  }

  /**
   * The conversation is over and cannot be resumed. Release everything.
   *
   * ── WHY THIS EXISTS ────────────────────────────────────────────────────────
   *
   * Reporting the error was not enough. The consumer's handler only records it
   * (`error: (code) => setError(code)`), so on a terminal failure nothing ran
   * that released the microphone. Observed in a real browser: 45 seconds after
   * the network went away — three times this client's own 15.5s of reconnect
   * backoff — `MediaStreamTrack.readyState` was still "live", with the OS
   * recording indicator on, behind a connection that would never recover.
   *
   * Teardown must not depend on a consumer remembering to call stop(). The
   * client knows the conversation is dead; it owns the resources; it releases
   * them here.
   */
  /**
   * Error codes that are NOT terminal.
   *
   * BAD_FRAME is the relay complaining about ONE frame; it explicitly continues
   * ("Refused, never repaired ... return"). Treating it as fatal turned a
   * recoverable protocol complaint into a killed conversation with a live
   * microphone torn down and "Something went wrong" on screen — which is what
   * happened in the failed physical test. Surface it, do not end the session.
   */
  private static readonly RECOVERABLE_ERRORS = new Set(['BAD_FRAME']);

  private fail(code: string): void {
    if (this.fatal) return;
    this.fatal = true;
    this.setState('error');
    // Starts immediately; the error reaches the UI without waiting for the
    // AudioContext to finish closing.
    void this.teardown();
    this.opts.handlers.error?.(code);
  }

  // ── liveness ─────────────────────────────────────────────────────────────
  //
  // A socket can stay "open" while nothing flows: the browser answers the
  // server's pings below the page, so a lost path never fires onclose. The
  // microphone then stayed live behind a conversation that could not recover
  // (e2e privacy G). The server now says `keepalive` every 15 s; when NOTHING
  // has arrived for LIVENESS_TIMEOUT_MS the socket is treated as lost and the
  // ordinary path runs — bounded reconnects, then fail(), which releases the
  // microphone and the AudioContext.
  private static readonly LIVENESS_TIMEOUT_MS = 40_000;
  /** How long an End pressed during connection waits for the socket to open. */
  private static readonly END_OPEN_WAIT_MS = 3_000;
  private lastInboundAt = 0;
  private livenessTimer: ReturnType<typeof setInterval> | null = null;

  private armLiveness(ws: WebSocket): void {
    this.disarmLiveness();
    this.lastInboundAt = Date.now();
    this.livenessTimer = setInterval(() => {
      if (this.ws !== ws || ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastInboundAt < RealtimeClient.LIVENESS_TIMEOUT_MS) return;
      this.disarmLiveness();
      this.lastCloseCode = 4408; // local: no inbound traffic — reported on the next realtime.start
      ws.onclose = null;
      try { ws.close(); } catch { /* already closing */ }
      if (this.closedByUser || this.fatal) return;
      this.scheduleReconnect();
    }, 5_000);
  }

  private disarmLiveness(): void {
    if (this.livenessTimer !== null) clearInterval(this.livenessTimer);
    this.livenessTimer = null;
  }

  private scheduleReconnect(): void {
    const max = this.opts.maxReconnects ?? 5;
    if (this.reconnectAttempts >= max) {
      this.fail('CONNECTION_LOST');
      return;
    }
    this.setState('reconnecting');
    const attempt = this.reconnectAttempts++;
    this.reconnectAttemptsMade = this.reconnectAttempts;
    // Bounded and deterministic. Transport retry timing is operational, not
    // domain state, so it carries no randomness that a test would have to mock.
    const delay = Math.min(500 * 2 ** attempt, 8000);
    setTimeout(() => { if (!this.closedByUser) this.connect(); }, delay);
  }

  private onAssistantAudio(buf: ArrayBuffer): void {
    if (!this.playbackNode || !this.activeResponse) return;
    const samples = decodePcm16(buf);
    this.playbackNode.port.postMessage(
      { type: 'enqueue', responseRef: this.activeResponse, samples },
      [samples.buffer]
    );
  }

  private onServerControl(raw: string): void {
    let frame: Record<string, unknown>;
    try { frame = JSON.parse(raw) as Record<string, unknown>; } catch { return; }
    const h = this.opts.handlers;

    switch (frame.type) {
      case 'state': this.setState(this.mapState(String(frame.state))); return;
      case 'transcript.partial': h.partialTranscript?.(String(frame.text)); return;
      case 'transcript.final': h.finalTranscript?.(String(frame.text)); return;
      case 'assistant.text.delta': h.assistantText?.(String(frame.text)); return;
      case 'assistant.started':
        this.activeResponse = String(frame.responseRef);
        this.assistantStartedAt = Date.now();
        this.interruptSent = false;
        this.speechFrames = 0;
        this.playbackNode?.port.postMessage({ type: 'begin', responseRef: this.activeResponse });
        this.setState('speaking');
        return;
      case 'audio.flush':
        this.playbackNode?.port.postMessage({ type: 'flush' });
        return;
      case 'assistant.completed':
      case 'assistant.interrupted':
        this.activeResponse = null;
        this.setState('listening');
        return;
      case 'degraded': h.error?.('DEGRADED'); return;
      // A server-side fatal is terminal in exactly the same way a lost
      // connection is: the conversation cannot continue, so the microphone goes
      // with it rather than waiting on the consumer to tear down.
      case 'error': {
        const code = String(frame.code);
        if (RealtimeClient.RECOVERABLE_ERRORS.has(code)) {
          // Reported so a staging failure is diagnosable, without ending a
          // conversation the server is still willing to serve.
          h.error?.(code);
          return;
        }
        this.fail(code);
        return;
      }
      default: return; // unknown server frames are ignored, never guessed at
    }
  }

  /** Server transport states are internal; the UI gets plain words. */
  private mapState(s: string): RealtimeUiState {
    switch (s) {
      case 'CONNECTING': return 'connecting';
      case 'READY': return 'ready';
      case 'LISTENING': return 'listening';
      case 'PROCESSING': return 'thinking';
      case 'SPEAKING': return 'speaking';
      case 'INTERRUPTING': return 'listening';
      case 'RECONNECTING': return 'reconnecting';
      case 'ENDING': return 'ending';
      case 'COMPLETED': return 'completed';
      case 'FAILED': return 'error';
      default: return this.state;
    }
  }

  // ── sending ──────────────────────────────────────────────────────────────

  private untilOpen(ws: WebSocket, ms: number): Promise<void> {
    return new Promise((resolve) => {
      const done = (): void => {
        clearTimeout(timer);
        ws.removeEventListener('open', done);
        ws.removeEventListener('close', done);
        resolve();
      };
      const timer = setTimeout(done, ms);
      ws.addEventListener('open', done);
      ws.addEventListener('close', done);
    });
  }

  private sendControl(frame: Record<string, unknown>): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(frame));
  }

  /**
   * Send one microphone packet, respecting backpressure.
   *
   * Above the high-water mark transmission pauses rather than queueing in
   * JavaScript memory. Frames are dropped only past the hard limit, and the
   * interval is recorded as degraded so it can be excluded from acoustic
   * evidence — audio with a hole in it still looks like speech, and an analyzer
   * would happily measure a pause that never happened.
   */
  private sendMicPacket(pcm: ArrayBuffer): void {
    const ws = this.ws;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;

    const buffered = ws.bufferedAmount;
    if (buffered > MIC_HARD_LIMIT_BYTES) {
      if (this.degradedFrom === null) this.degradedFrom = Date.now();
      return;
    }
    if (buffered > MIC_HIGH_WATER_BYTES) {
      this.micPaused = true;
      if (this.degradedFrom === null) this.degradedFrom = Date.now();
      return;
    }
    if (this.micPaused && buffered > MIC_LOW_WATER_BYTES) return;
    this.micPaused = false;
    this.degradedFrom = null;

    this.micSequence++;
    ws.send(pcm);
  }

  /** Intervals where microphone audio was incomplete. Diagnostics only. */
  get degradedSince(): number | null {
    return this.degradedFrom;
  }
}
